import MenuIcon from "@mui/icons-material/Menu";
import {
  AppBar,
  Box,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography
} from "@mui/material";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import DashboardIcon from "@mui/icons-material/Dashboard";
import DescriptionIcon from "@mui/icons-material/Description";
import PaymentIcon from "@mui/icons-material/Payment";
import LogoutIcon from "@mui/icons-material/Logout";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import HistoryIcon from "@mui/icons-material/History";
import { useState, useMemo } from "react";
import { useLocation, Navigate, Outlet, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../store/auth-store";
import { canAccessPath, type UserRole } from "../../utils/permissions";

const drawerWidth = 240;

const allMenuItems = [
  { label: "Dashboard", path: "/", icon: <DashboardIcon fontSize="small" /> },
  { label: "Cases", path: "/cases", icon: <PeopleAltIcon fontSize="small" /> },
  { label: "Applications", path: "/applications", icon: <DescriptionIcon fontSize="small" /> },
  { label: "Paysheets", path: "/paysheets", icon: <PaymentIcon fontSize="small" /> },
  { label: "Tasks", path: "/tasks", icon: <AssignmentTurnedInIcon fontSize="small" /> },
  { label: "Programs", path: "/programs", icon: <DescriptionIcon fontSize="small" /> },
  { label: "Participants", path: "/participants", icon: <PeopleAltIcon fontSize="small" /> },
  { label: "Users", path: "/users", icon: <ManageAccountsIcon fontSize="small" /> },
  { label: "Audit Logs", path: "/audit-logs", icon: <HistoryIcon fontSize="small" /> }
];

export const ShellLayout = () => {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const signOut = useAuthStore((state) => state.signOut);
  const session = useAuthStore((state) => state.session);
  const location = useLocation();

  // Filter menu items based on user role
  const menuItems = useMemo(() => {
    if (!session) return [];
    const userRole = session.user.role as UserRole;
    return allMenuItems.filter(item => canAccessPath(userRole, item.path));
  }, [session]);

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  if (!session) {
    return <Navigate to="/auth/login" replace />;
  }

  if (location.pathname === "/") {
    return <Outlet />;
  }

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          SEED IMS
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.label} disablePadding>
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
                selected={location.pathname === item.path}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
        ))}
      </List>
      <Divider />
      <List sx={{ marginTop: "auto" }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              signOut();
              navigate("/auth/login");
            }}
          >
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Sign out" />
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          display: { xs: "block", sm: "none" } // Only show on mobile
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            SEED Information Management System
          </Typography>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="navigation"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth }
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              borderRight: "none" // Remove border to match dashboard
            }
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: "100vh",
          backgroundColor: (theme) =>
            theme.palette.mode === "light" ? theme.palette.grey[100] : theme.palette.background.default,
          display: "flex",
          flexDirection: "column"
        }}
      >
        <Toolbar sx={{ display: { xs: "block", sm: "none" } }} />
        <Container maxWidth="xl" sx={{ flexGrow: 1 }}>
          <Outlet />
        </Container>
        <Box
          component="footer"
          sx={{
            py: 2,
            px: 2,
            mt: 4,
            textAlign: "center",
            borderTop: "1px solid",
            borderColor: "divider"
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Platform Built by{" "}
            <a
              href="https://trusttechdigital.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "inherit", fontWeight: 500 }}
            >
              TrustTech Digital
            </a>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
